import { useState, useEffect, useRef } from "react";

import {
    trackBannerImpression,
    trackBannerClick,
} from "../../services/analyticsService";

import { getVisitorId } from "../../utils/visitorId";

const Banner = ({ banner }) => {
    const [clicked, setClicked] = useState(false);

    const bannerRef = useRef(null);
    const visitorId = getVisitorId();

    useEffect(() => {
        const element = bannerRef.current;

        if (!element) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    trackBannerImpression({
                        visitor_id: visitorId,
                        banner_id: banner.id,
                        blog_id: banner.blog_id,
                    });

                    observer.disconnect();
                }
            },
            {
                threshold: 0.5,
            }
        );

        observer.observe(element);

        return () => observer.disconnect();
    }, [
        banner.id,
        banner.blog_id,
        visitorId,
    ]);

    const handleClick = async () => {
        if (clicked) return;

        setClicked(true);

        await trackBannerClick({
            visitor_id: visitorId,
            banner_id: banner.id,
            blog_id: banner.blog_id,
        });

        window.location.href = banner.cta_url;
    };

    return (
        <div
            ref={bannerRef}
            className="
                relative my-10 overflow-hidden rounded-2xl
                bg-gradient-to-br from-blue-900 via-blue-800 to-blue-700
                text-white shadow-2xl
                transition-all duration-300
                hover:scale-[1.01] hover:shadow-2xl
            "
        >
            {/* Animated deep blue background */}
            <div className="absolute inset-0 opacity-25">
                <div className="
                    absolute -top-20 -right-20
                    h-64 w-64 rounded-full
                    bg-blue-600 blur-3xl animate-pulse
                " />

                <div className="
                    absolute -bottom-20 -left-20
                    h-64 w-64 rounded-full
                    bg-blue-500 blur-3xl animate-pulse delay-1000
                " />
            </div>

            {/* Glossy overlay */}
            <div className="
                pointer-events-none absolute inset-0
                bg-gradient-to-t from-black/20 via-transparent to-white/10
            " />

            <div className="relative flex flex-col items-stretch md:flex-row">

                {banner.image && (
                    <div className="relative w-full shrink-0 overflow-hidden md:w-64">
                        <img
                            src={banner.image}
                            alt={banner.title}
                            className="
                                h-48 w-full object-cover
                                transition-transform duration-500
                                hover:scale-105 md:h-full
                            "
                        />

                        {/* Deep blue image overlay */}
                        <div className="
                            absolute inset-0
                            bg-gradient-to-r from-transparent to-blue-950/30
                            md:bg-gradient-to-l
                        " />
                    </div>
                )}

                <div className="relative z-10 flex-1 p-6 md:p-8">

                    {/* Badge */}
                    <div className="
                        mb-3 inline-flex items-center gap-2
                        rounded-full border border-blue-300/20
                        bg-blue-950/30 px-3 py-1
                        text-xs font-semibold uppercase tracking-wider
                        backdrop-blur-sm
                    ">
                        <span className="relative flex h-2 w-2">
                            <span className="
                                absolute inline-flex h-full w-full
                                animate-ping rounded-full
                                bg-green-400 opacity-75
                            " />

                            <span className="
                                relative inline-flex h-2 w-2
                                rounded-full bg-green-400
                            " />
                        </span>

                        Featured Offer
                    </div>

                    <h3 className="
                        text-2xl font-extrabold
                        leading-tight tracking-tight
                        md:text-3xl
                    ">
                        🚀 {banner.title}
                    </h3>

                    <p className="
                        mt-3 max-w-2xl
                        text-sm leading-relaxed text-white/90
                        md:text-base
                    ">
                        {banner.message}
                    </p>

                    <div className="mt-6 flex flex-wrap items-center gap-4">

                        <button
                            onClick={handleClick}
                            disabled={clicked}
                            className={`
                                relative group overflow-hidden rounded-xl
                                bg-white px-6 py-3
                                font-bold text-blue-800
                                transition-all duration-300
                                md:px-8 md:py-3.5
                                hover:scale-105 hover:bg-blue-50
                                hover:shadow-xl active:scale-95
                                ${
                                    clicked
                                        ? 'cursor-not-allowed opacity-50'
                                        : ''
                                }
                            `}
                        >
                            {/* Button shine effect */}
                            <span className="
                                absolute inset-0 -translate-x-full
                                bg-gradient-to-r
                                from-transparent via-blue-100/50 to-transparent
                                transition-transform duration-1000
                                group-hover:translate-x-full
                            " />

                            <span className="
                                relative flex items-center gap-2
                            ">
                                {clicked ? (
                                    <>
                                        <svg
                                            className="h-5 w-5 animate-spin"
                                            viewBox="0 0 24 24"
                                        >
                                            <circle
                                                className="opacity-25"
                                                cx="12"
                                                cy="12"
                                                r="10"
                                                stroke="currentColor"
                                                strokeWidth="4"
                                                fill="none"
                                            />

                                            <path
                                                className="opacity-75"
                                                fill="currentColor"
                                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                            />
                                        </svg>

                                        Processing...
                                    </>
                                ) : (
                                    <>
                                        {banner.cta_text}

                                        <svg
                                            className="
                                                h-4 w-4
                                                transition-transform
                                                group-hover:translate-x-1
                                            "
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth="2"
                                                d="M13 7l5 5m0 0l-5 5m5-5H6"
                                            />
                                        </svg>
                                    </>
                                )}
                            </span>
                        </button>

                        <span className="
                            flex items-center gap-1
                            text-xs text-blue-100/70
                        ">
                            <svg
                                className="h-3 w-3"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                            >
                                <path
                                    fillRule="evenodd"
                                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                                    clipRule="evenodd"
                                />
                            </svg>

                            Limited time offer
                        </span>
                    </div>
                </div>
            </div>

            {/* Decorative corner accents */}
            <div className="
                pointer-events-none absolute right-0 top-0
                h-16 w-16
                rounded-tr-2xl
                border-r-2 border-t-2 border-blue-300/20
            " />

            <div className="
                pointer-events-none absolute bottom-0 left-0
                h-16 w-16
                rounded-bl-2xl
                border-b-2 border-l-2 border-blue-300/20
            " />
        </div>
    );
};

export default Banner;